import { useState, useEffect, useRef, useCallback } from 'react';
import type {
  SortingState,
  ColumnSizingState,
  ColumnFiltersState,
  PaginationState,
} from '@tanstack/react-table';
import sessionStorageService from '../../../services/sessionStorageService';

type UseDataGridStateProps = {
  page: number;
  pageSize: number;
  serverSide: boolean;
  onSortChange?: (sorting: SortingState) => void;
  onSortingChange?: (sorting: SortingState) => void;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  data: unknown[];
  columns: unknown[];
  filterResetTrigger?: number;
  initialSorting?: SortingState;
  initialColumnFilters?: ColumnFiltersState;
  columnFiltersSessionKey?: string;
  initialColumnSizing?: ColumnSizingState;
  onColumnSizingChange?: (sizing: ColumnSizingState) => void;
};

type UseDataGridStateReturn = {
  sorting: SortingState;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  columnSizing: ColumnSizingState;
  setColumnSizing: React.Dispatch<React.SetStateAction<ColumnSizingState>>;
  columnFilters: ColumnFiltersState;
  setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  pagination: PaginationState;
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
  headerHeight: number;
  headerRowRef: React.RefObject<HTMLTableRowElement | null>;
  handlePageChange: (event: unknown, newPage: number) => void;
  handlePageSizeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

/**
 * Hook to manage DataGrid state (sorting, pagination, column sizing, filters)
 */
export const useDataGridState = ({
  page,
  pageSize,
  serverSide,
  onSortChange,
  onSortingChange,
  onPageChange,
  onPageSizeChange,
  data,
  columns,
  filterResetTrigger,
  initialSorting,
  initialColumnFilters,
  columnFiltersSessionKey,
  initialColumnSizing,
  onColumnSizingChange,
}: UseDataGridStateProps): UseDataGridStateReturn => {
  const [sorting, setSorting] = useState<SortingState>(() => initialSorting ?? []);
  const [columnSizing, setColumnSizingInternal] = useState<ColumnSizingState>(
    () => initialColumnSizing ?? {}
  );
  // Wrap setColumnSizing to also notify parent
  const setColumnSizing = useCallback<React.Dispatch<React.SetStateAction<ColumnSizingState>>>(
    (updater) => {
      setColumnSizingInternal((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        onColumnSizingChange?.(next);
        return next;
      });
    },
    [onColumnSizingChange]
  );

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    () => initialColumnFilters ?? []
  );
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: page,
    pageSize: pageSize,
  });
  const [headerHeight, setHeaderHeight] = useState<number>(40);
  const headerRowRef = useRef<HTMLTableRowElement>(null);

  // Sync sorting/sizing when persisted state arrives from DB (adjust-state-during-render)
  const [prevInitialSorting, setPrevInitialSorting] = useState(initialSorting);
  const [prevInitialColumnSizing, setPrevInitialColumnSizing] = useState(initialColumnSizing);
  const [prevInitialColumnFilters, setPrevInitialColumnFilters] = useState(initialColumnFilters);
  const previousInitialFiltersRef = useRef(initialColumnFilters);

  if (initialSorting !== prevInitialSorting) {
    setPrevInitialSorting(initialSorting);
    if (initialSorting && initialSorting.length > 0) {
      setSorting(initialSorting);
    }
  }

  if (initialColumnSizing !== prevInitialColumnSizing) {
    setPrevInitialColumnSizing(initialColumnSizing);
    if (initialColumnSizing) {
      setColumnSizingInternal(initialColumnSizing);
    }
  }

  if (initialColumnFilters !== prevInitialColumnFilters) {
    setPrevInitialColumnFilters(initialColumnFilters);
    if (initialColumnFilters) {
      setColumnFilters(initialColumnFilters);
    }
  }

  useEffect(() => {
    if (!columnFiltersSessionKey) return;
    if (previousInitialFiltersRef.current !== initialColumnFilters) {
      previousInitialFiltersRef.current = initialColumnFilters;
      return;
    }
    if (columnFilters.length > 0) {
      sessionStorageService.save(columnFiltersSessionKey, columnFilters);
    } else {
      sessionStorageService.remove(columnFiltersSessionKey);
    }
  }, [columnFilters, columnFiltersSessionKey, initialColumnFilters]);

  // Sync pagination with external props
  // Server-side: sync both page index and page size from parent.
  // Client-side: only sync page size (for persisted state applied after mount).
  useEffect(() => {
    if (serverSide) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Syncing server-controlled pagination state
      setPagination({ pageIndex: page, pageSize: pageSize });
    } else {
      setPagination((prev) => (prev.pageSize === pageSize ? prev : { ...prev, pageSize }));
    }
  }, [page, pageSize, serverSide]);

  // Measure header height for sticky filter row positioning
  useEffect(() => {
    if (headerRowRef.current) {
      const height = headerRowRef.current.offsetHeight;
      setHeaderHeight(height);
    }
  }, [data, columns]);

  // Handle sort changes
  useEffect(() => {
    onSortChange?.(sorting);
  }, [sorting, onSortChange]);

  // Notify parent of sorting changes (always, for state persistence)
  useEffect(() => {
    onSortingChange?.(sorting);
  }, [sorting, onSortingChange]);

  // Clear column filters when reset is triggered
  // Note: This responds to an external reset action (e.g., "Reset Filters" button).
  // The parent component increments filterResetTrigger to trigger this cleanup.
  useEffect(() => {
    if (filterResetTrigger !== undefined && filterResetTrigger > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Responding to external reset trigger
      setColumnFilters([]);
      setSorting([]);
    }
  }, [filterResetTrigger]);

  // Handle page changes
  const handlePageChange = (_event: unknown, newPage: number) => {
    if (onPageChange) {
      onPageChange(newPage);
    } else if (!serverSide) {
      setPagination((prev) => ({ ...prev, pageIndex: newPage }));
    }
  };

  // Handle page size changes
  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(event.target.value, 10);
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
    if (!serverSide) {
      setPagination((prev) => ({ ...prev, pageSize: newSize, pageIndex: 0 }));
    }
  };

  return {
    sorting,
    setSorting,
    columnSizing,
    setColumnSizing,
    columnFilters,
    setColumnFilters,
    pagination,
    setPagination,
    headerHeight,
    headerRowRef,
    handlePageChange,
    handlePageSizeChange,
  };
};
